'use client';

import { PATH } from '@/constants';
import { useAuth } from '@/contexts';
import { supabaseClient } from '@/utils';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

export const withAuth = <P extends object>(Component: React.ComponentType<P>) => (props: P) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isValidRoute, setIsValidRoute] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const { isAuthenticated, isSessionInitializing } = useAuth();

  // pathname/searchParams are only read to build the MFA-challenge returnUrl in
  // the rare aal1->aal2 branch below. A ref (not a reactive dependency) keeps
  // them current without making every in-app URL sync -- e.g. ChatHub's
  // history.replaceState() to reflect ?conversation_id= -- retrigger the whole
  // effect below. That retrigger used to call setIsValidRoute(false) synchronously,
  // rendering null and hard-unmounting the entire authenticated app tree on every
  // query-string change, then remounting it fresh once MFA re-verified. Confirmed
  // live: this is what discarded an in-flight chat send the instant its URL sync fired.
  const routeRef = useRef({ pathname, searchParams });
  routeRef.current = { pathname, searchParams };

  // Wait for hydration to complete
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // eslint-disable-next-line no-console
    console.log('[DIAG][withAuth] verification effect fired', {
      isHydrated, isSessionInitializing, isAuthenticated, timestamp: new Date().toISOString(),
    });

    // CRITICAL FIX: Only perform auth check after hydration AND session initialization
    // This prevents redirect before Supabase session is synced to localStorage (SSO flow)
    if (!isHydrated) return;
    if (isSessionInitializing) return; // Wait for session to be checked

    // Allow access to onboarding routes for workspace setup
    // These routes are needed for new users to complete their onboarding

    if (!isAuthenticated) {
      setIsValidRoute(false);
      router.replace(PATH.LOGIN);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[DIAG][withAuth] setIsValidRoute(false) -- app tree unmounts until MFA re-verifies', {
      timestamp: new Date().toISOString(),
    });
    setIsValidRoute(false);

    const verifyMfaAssurance = async () => {
      try {
        const { data: assurance, error } =
          await supabaseClient.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;

        if (error || !assurance) {
          throw error ?? new Error('Supabase returned no MFA assurance level');
        }

        if (assurance.currentLevel === 'aal1' && assurance.nextLevel === 'aal2') {
          const { pathname: currentPathname, searchParams: currentSearchParams } = routeRef.current;
          const query = currentSearchParams?.toString();
          const returnUrl = `${currentPathname}${query ? `?${query}` : ''}`;
          router.replace(`${PATH.MFA}?returnUrl=${encodeURIComponent(returnUrl)}`);
          return;
        }

        // eslint-disable-next-line no-console
        console.log('[DIAG][withAuth] setIsValidRoute(true) -- app tree remounts fresh', {
          timestamp: new Date().toISOString(),
        });
        setIsValidRoute(true);
      } catch {
        if (cancelled) return;

        // Fail closed when assurance cannot be established. Signing out prevents
        // the public-route guard from immediately bouncing the user back into the app.
        await supabaseClient.auth.signOut().catch(() => undefined);
        if (!cancelled) {
          const params = new URLSearchParams({
            error: 'mfa_assurance_check_failed',
            message: 'We could not verify your MFA status. Please sign in again.',
          });
          router.replace(`${PATH.LOGIN}?${params.toString()}`);
        }
      }
    };

    void verifyMfaAssurance();

    return () => {
      cancelled = true;
    };
  }, [isHydrated, isSessionInitializing, isAuthenticated, router]);

  // Don't render anything until hydration is complete and session is initialized
  if (!isHydrated || isSessionInitializing) return null;

  return isValidRoute ? <Component {...props} /> : null;
};
